import { DiscoveryKeyword } from '@teko/discovery';
import { trackManually, trackSearchEvent } from '@teko/tracking';
import { normalizeText } from '@teko/utils';
import { SEARCH_ALGO } from 'constants/index';
import { useCartV2, useGlobalData } from 'contexts';
import { useKeywords } from 'hooks';
import { useRouter } from 'next/router';
import { encode } from 'querystring';
import { useEffect, useMemo, useState } from 'react';
import { useSearchAlgo } from './useSearchAlgo';

interface Option {
  numberOfHistories?: number;
  limitKeywords?: number;
}

interface TargetPath {
  pathname: string;
  query: {
    router: string;
    query?: string;
    masterCategoryIds?: string;
  };
}

export const useSearchSuggestions = ({
  numberOfHistories = 5,
  limitKeywords,
}: Option = {}) => {
  const {
    isMobile,
    keywordToCollectionMapping: wlKeywordToCollectionMapping,
    enableSearchAlgoABTesting,
  } = useGlobalData();
  const router = useRouter();
  const searchHistoryKey = `${isMobile ? 'MOBILE' : 'DESKTOP'}_SEARCH_HISTORY`;
  const initKeyword = (router.query.query as string) || '';
  const { onAddSingleItem, onRequestItemSku } = useCartV2();
  const { getSearchAlgoFromCookie } = useSearchAlgo();
  const [keyword, setKeyword] = useState(initKeyword);
  const [histories, setHistories] = useState<DiscoveryKeyword[]>([]);
  const { result: suggestions, isSuggestProduct } = useKeywords(
    keyword,
    limitKeywords,
  );

  // https://jira.teko.vn/browse/UX4BUYER-5849
  const keywordToCollectionMapping = useMemo(() => {
    const mapping: Map<string, string> = new Map(wlKeywordToCollectionMapping);
    return mapping;
  }, [wlKeywordToCollectionMapping]);

  const getSearchHistory = (count = 5): DiscoveryKeyword[] => {
    if (typeof localStorage !== 'undefined') {
      const historyStorage = localStorage.getItem(searchHistoryKey);
      if (historyStorage) {
        const data = JSON.parse(historyStorage);
        return (data || []).reverse().splice(0, count);
      }
      return [];
    }
    return [];
  };
  const saveSearchHistory = (keyword: DiscoveryKeyword): void => {
    if (!keyword) return;
    const historyStorage = localStorage.getItem(searchHistoryKey);
    if (!historyStorage) {
      localStorage.setItem(searchHistoryKey, JSON.stringify([keyword]));
      return;
    }
    let historyArr = JSON.parse(historyStorage);
    historyArr = historyArr.filter(
      (item: DiscoveryKeyword) =>
        JSON.stringify(item) !== JSON.stringify(keyword),
    );
    historyArr = [...historyArr, keyword];
    localStorage.setItem(searchHistoryKey, JSON.stringify(historyArr));
    setHistories(getSearchHistory(numberOfHistories));
  };
  const removeSearchHistories = () => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(searchHistoryKey, JSON.stringify([]));
      setHistories([]);
    }
  };

  const onSearch = (keywordObj: DiscoveryKeyword): void => {
    const searchKeyword = keywordObj.query;
    saveSearchHistory(keywordObj);

    // if mapping contain keyword, go to that collection
    const normalizedKeyword = normalizeText(searchKeyword as string, false);
    const localSearchAlgo = getSearchAlgoFromCookie();
    if (
      keywordToCollectionMapping.size &&
      keywordToCollectionMapping.has(normalizedKeyword)
    ) {
      const destination = keywordToCollectionMapping.get(
        normalizedKeyword,
      ) as string;
      router.push(destination).then(() => {
        window.scrollTo(0, 0);
      });

      trackSearchEvent(searchKeyword || '');
      trackManually(
        'searchButton',
        'headerBar',
        'searchProductResult',
        JSON.stringify({
          searchKeyword,
          href: window.location.href,
          url: window.location.origin + destination,
        }),
      );
    } else {
      // keep searching flow as usual
      const targetPath: TargetPath = {
        pathname: '/search',
        query: {
          router: 'productListing',
          query: searchKeyword,
        },
      };

      const targetAsPath = {
        ...targetPath,
        query: encode(targetPath.query),
      };

      if (router.query.query) {
        router.replace(targetPath, targetAsPath);
      } else {
        router.push(targetPath, targetAsPath);
      }

      const targetUrl = new URL(window.location.origin);
      targetUrl.pathname = targetPath.pathname;
      targetUrl.search = new URLSearchParams(targetPath.query).toString();

      trackSearchEvent(
        searchKeyword || '',
        // if flag enableSearchAlgoABTesting===true --> send search algo version in tracking event
        !!enableSearchAlgoABTesting
          ? JSON.stringify({
              searchAlgorithm: !!localSearchAlgo
                ? localSearchAlgo.replace(SEARCH_ALGO.REPLACE_PATTERN, '')
                : localSearchAlgo,
            })
          : undefined,
      );
      trackManually(
        'searchButton',
        'headerBar',
        'searchProductResult',
        JSON.stringify({
          searchKeyword,
          href: window.location.href,
          url: targetUrl.href,
        }),
      );
    }
  };

  const addToCart = async (product: DiscoveryKeyword, quantity = 1) => {
    if (!!product.sku) {
      await onAddSingleItem(
        // should pass product.sellerSku but api get keyword (discovery) hasn't returned sellerSku yet
        { sku: product.sku, sellerSku: product.sku, quantity },
        true,
      );
    } else if (!!product.highlight) {
      await onRequestItemSku(product.highlight, quantity);
    }
  };

  const onRedirectDetailProduct = (sku?: string): void => {
    if (sku) {
      router.push(`/products/${sku}`);
    }
  };

  useEffect(() => {
    if (numberOfHistories > 0) {
      setHistories(getSearchHistory(numberOfHistories));
    } else setHistories([]);
  }, [numberOfHistories]);

  return {
    keyword,
    setKeyword,
    histories,
    removeSearchHistories,
    suggestions: suggestions,
    onSearch,
    onRedirectDetailProduct,
    isSuggestProduct,
    addToCart,
  };
};
